import React from 'react';

/**
 * PageHeader Component
 * 
 * Consistent header for all pages with icon, title, subtitle, badge, and actions.
 * Follows BankHub design pattern from successful implementations.
 * 
 * @example
 * ```tsx
 * <PageHeader
 *   icon={<Database size={24} />}
 *   title="Database Sync"
 *   subtitle="Manage your data synchronization"
 *   badge={<StatusBadge status="success" label="Active" />}
 *   actions={<Button>Sync Now</Button>}
 * />
 * ```
 */

export interface PageHeaderProps {
  /** Icon element (from lucide-react) */
  icon?: React.ReactNode;
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Badge or status indicator */
  badge?: React.ReactNode;
  /** Action buttons (right side) */
  actions?: React.ReactNode;
  /** Additional className */
  className?: string;
  /** Breadcrumbs or navigation */
  breadcrumbs?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  badge,
  actions,
  className = '',
  breadcrumbs,
}) => {
  return (
    <div className={`mb-4 ${className}`}>
      {/* Breadcrumbs (if provided) */}
      {breadcrumbs && (
        <div className="mb-4">
          {breadcrumbs}
        </div>
      )}

      {/* Main Header */}
      <div className="flex items-start justify-between gap-4">
        {/* Left: Icon, Title, Subtitle, Badge */}
        <div className="flex items-start gap-4 flex-1">
          {/* Icon - using design tokens */}
          {icon && (
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-primary text-ds-text-inverse flex items-center justify-center shadow-elev-2">
                {icon}
              </div>
            </div>
          )}

          {/* Title, Subtitle, Badge - using design tokens */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-ds-text-primary">
                {title}
              </h1>
              {badge && <div>{badge}</div>}
            </div>

            {subtitle && (
              <p className="mt-2 text-base text-ds-text-secondary max-w-3xl">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        {actions && (
          <div className="flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

PageHeader.displayName = 'PageHeader';

export default PageHeader;
